import { $ } from '../utils/dom.js';
import { onLenisScroll } from '../utils/lenis-scroll.js';

const HEADER_SELECTOR = '.header';
const HIDDEN_CLASS = 'header--hidden';
const SCROLLED_CLASS = 'header--scrolled';
const CSS_VAR = '--header-height';
const SCROLL_DELTA = 8;
const SCROLLED_DELAY = 300;

const getScrollY = () => window.scrollY || document.documentElement.scrollTop || 0;

export function initHeader() {
  const header = $(HEADER_SELECTOR);

  if (!header) {
    return () => {};
  }

  const root = document.documentElement;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setHeight = () => {
    root.style.setProperty(CSS_VAR, `${header.offsetHeight}px`);
  };

  setHeight();

  const observer = new ResizeObserver(setHeight);
  observer.observe(header);

  let removeLenisListener = null;
  let removeNativeListener = null;
  let scrolledTimeout = null;
  let lastScrollY = 0;

  const isOverlayOpen = () => {
    const navPanel = $('[data-nav-panel]', header);
    const bookPanel = $('[data-book-table-panel]', header);

    return (
      navPanel?.getAttribute('data-state') === 'open'
      || bookPanel?.getAttribute('data-state') === 'open'
    );
  };

  const updateHeaderScrolled = (scrollY) => {
    if (scrollY <= 0) {
      clearTimeout(scrolledTimeout);
      scrolledTimeout = null;
      header.classList.remove(SCROLLED_CLASS);
      return;
    }

    if (header.classList.contains(SCROLLED_CLASS) || scrolledTimeout) {
      return;
    }

    scrolledTimeout = setTimeout(() => {
      if (getScrollY() > 0) {
        header.classList.add(SCROLLED_CLASS);
      }

      scrolledTimeout = null;
    }, SCROLLED_DELAY);
  };

  const updateHeaderVisibility = (scrollY) => {
    updateHeaderScrolled(scrollY);

    if (prefersReducedMotion) {
      return;
    }

    if (isOverlayOpen() || scrollY <= 0) {
      header.classList.remove(HIDDEN_CLASS);
      lastScrollY = scrollY;
      return;
    }

    if (scrollY <= window.innerHeight) {
      header.classList.remove(HIDDEN_CLASS);
      lastScrollY = scrollY;
      return;
    }

    const delta = scrollY - lastScrollY;

    if (Math.abs(delta) < SCROLL_DELTA) {
      return;
    }

    if (delta > 0) {
      header.classList.add(HIDDEN_CLASS);
    } else {
      header.classList.remove(HIDDEN_CLASS);
    }

    lastScrollY = scrollY;
  };

  removeLenisListener = onLenisScroll(({ scroll }) => {
    updateHeaderVisibility(scroll);
  });

  if (prefersReducedMotion || window.matchMedia('(max-width: 768px)').matches) {
    const onNativeScroll = () => updateHeaderVisibility(getScrollY());

    window.addEventListener('scroll', onNativeScroll, { passive: true });
    removeNativeListener = () => window.removeEventListener('scroll', onNativeScroll);
  }

  updateHeaderVisibility(getScrollY());

  return () => {
    clearTimeout(scrolledTimeout);
    observer.disconnect();
    removeLenisListener?.();
    removeNativeListener?.();
  };
}
